import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";

import { getCall_v2 } from "services";

import { StatusHistoryFilter, StatusHistoryTable } from "./components";
import { mapHistoryFile } from "./makeData";
import {
  DEFAULT_PAGE,
  DEFAULT_ROWS_PER_PAGE,
  FETCH_HISTORY_API_URL,
  FETCH_USERS_API_URL,
} from "./constants";

const BulkUploadStatusHistory = () => {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [uploadedByOptions, setUploadedByOptions] = useState([]);
  const [fileTypeOptions, setFileTypeOptions] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  useEffect(() => {
    getCall_v2(FETCH_USERS_API_URL)
      .then(({ data: response }) => {
        setUploadedByOptions(response?.users ?? []);
        setFileTypeOptions(response?.fileTypes ?? []);
      })
      .catch(() => {
        setUploadedByOptions([]);
        setFileTypeOptions([]);
      });
  }, []);

  // Fetches a page of upload requests history
  const fetchHistory = useCallback(async (filters, pageIndex, pageSize) => {
    setIsLoading(true);
    try {
      const { data: response } = await getCall_v2(FETCH_HISTORY_API_URL, {
        ...filters,
        page: pageIndex,
        pageSize,
      });

      const files = response?.data?.files ?? [];
      setRequests(files.map(mapHistoryFile));
      setTotalCount(response?.data?.pagination?.totalRecords ?? 0);
    } catch {
      setRequests([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory(appliedFilters, page, rowsPerPage);
  }, [appliedFilters, page, rowsPerPage, fetchHistory]);

  const handleFilterSubmit = useCallback((filters) => {
    setPage(DEFAULT_PAGE);
    setAppliedFilters(filters);
  }, []);

  const handleFilterClear = useCallback(() => {
    setPage(DEFAULT_PAGE);
    setAppliedFilters({});
  }, []);

  const handlePageChange = useCallback((_event, newPage) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(DEFAULT_PAGE);
  }, []);

  const handleViewDetails = useCallback(
    (request) => {
      navigate(`/bulk-upload-status-history-details/${request.requestId}`, {
        state: { request },
      });
    },
    [navigate],
  );

  const pagination = useMemo(
    () => ({ page, rowsPerPage, totalCount }),
    [page, rowsPerPage, totalCount],
  );

  return (
    <Box>
      <StatusHistoryFilter
        uploadedByOptions={uploadedByOptions}
        fileTypeOptions={fileTypeOptions}
        onSubmit={handleFilterSubmit}
        onClear={handleFilterClear}
      />
      <StatusHistoryTable
        isLoading={isLoading}
        requests={requests}
        pagination={pagination}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onViewDetails={handleViewDetails}
      />
    </Box>
  );
};

export default BulkUploadStatusHistory;
